/* eslint-disable no-nested-ternary */
import { useMemo } from 'react';

import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { useAuthContext } from 'src/auth/hooks';
// import Label from 'src/components/label';

import { TEAM_MEMBER_ROLE_LABELS } from 'src/_mock/_team';
import { useGetSubscriptionByUser } from 'src/api/subscription';
import { USER_ROLES_LABELS, SUBSCRIPTION_STATUS } from 'src/_mock';

// import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';

// ----------------------------------------------------------------------
// Define static navigation items
const NAV_ITEMS = (t) => ({
  USER: {
    title: t('user'),
    path: paths.dashboard.user.root,
    icon: ICONS.user,
    children: [
      { title: t('list'), path: paths.dashboard.user.list },
      { title: t('account'), path: paths.dashboard.user.account },
    ],
  },
  CUSTOMER: {
    title: t('customer'),
    path: paths.dashboard.customer.root,
    icon: ICONS.customer,
    children: [
      { title: t('list'), path: paths.dashboard.customer.list },
      { title: t('create'), path: paths.dashboard.customer.new },
    ],
  },
  SUBSCRIPTION: {
    title: t('subscription'),
    path: paths.dashboard.subscription.root,
    icon: ICONS.subscription,
    children: [{ title: t('list'), path: paths.dashboard.subscription.list }],
  },
  ORDER: {
    title: t('order'),
    path: paths.dashboard.order.root,
    icon: ICONS.order,
    children: [{ title: t('list'), path: paths.dashboard.order.root }],
  },
  CALENDAR: {
    title: t('appointments'),
    path: paths.dashboard.calendar,
    icon: ICONS.calendar,
  },
  INVOICE: {
    title: t('invoice'),
    path: paths.dashboard.invoice.root,
    icon: ICONS.invoice,
    children: [
      { title: t('list'), path: paths.dashboard.invoice.root },
      { title: t('create'), path: paths.dashboard.invoice.new },
    ],
  },
  ORGANIZATION: {
    title: t('organization'),
    path: paths.dashboard.organization.root,
    icon: ICONS.organization,
    children: [
      // This is accessible for every team member
      { title: t('team'), path: paths.dashboard.organization.root },
      // This is accessible for the owner only
      { title: t('create'), path: paths.dashboard.organization.new },
      // This is accessible for the owner only
      { title: t('edit'), path: paths.dashboard.organization.edit },
    ],
  },
  PATIENT: {
    title: t('patient'),
    path: paths.dashboard.patient.root,
    icon: ICONS.faceMask,
    children: [
      { title: t('list'), path: paths.dashboard.patient.root },
      { title: t('create'), path: paths.dashboard.patient.new },
    ],
  },
  SERVICE: {
    title: t('service'),
    path: paths.dashboard.service.root,
    icon: ICONS.service,
    children: [
      { title: t('list'), path: paths.dashboard.service.root },
      { title: t('create'), path: paths.dashboard.service.new },
    ],
  },
  DRUG: {
    title: t('drug'),
    path: paths.dashboard.drug.root,
    icon: ICONS.drug,
    children: [
      { title: t('list'), path: paths.dashboard.drug.root },
      { title: t('create'), path: paths.dashboard.drug.new },
    ],
  },
  PRESCRIPTION: {
    title: t('prescription'),
    path: paths.dashboard.prescription.root,
    icon: ICONS.prescription,
    children: [{ title: t('create'), path: paths.dashboard.prescription.new }],
  },
  KANBAN: {
    title: t('kanban'),
    path: paths.dashboard.kanban,
    icon: ICONS.kanban,
  },
});

// Define role-based navigation mapping
const ROLE_NAV_MAPPING = (t) => ({
  // USER WHO IS SUBSCRIBED
  [USER_ROLES_LABELS.ADMIN]: [
    'USER',
    'CALENDAR',
    'ORGANIZATION',
    'INVOICE',
    'PATIENT',
    'SERVICE',
    'DRUG',
    'PRESCRIPTION',
    'KANBAN',
  ],
  // USER WHO IS SUPER ADMIN
  SUPER_ADMIN: [
    'USER',
    'CALENDAR',
    'ORGANIZATION',
    'CUSTOMER',
    'SUBSCRIPTION',
    'ORDER',
    'INVOICE',
    'PATIENT',
    'SERVICE',
    'DRUG',
    'PRESCRIPTION',
    'KANBAN',
  ],
  [TEAM_MEMBER_ROLE_LABELS.RECEPTIONIST]: [
    'USER',
    'CALENDAR',
    'ORGANIZATION',
    'INVOICE',
    'PATIENT',
    'SERVICE',
    'DRUG',
    'PRESCRIPTION',
    'KANBAN',
  ],
  [TEAM_MEMBER_ROLE_LABELS.OWNER]: [
    'USER',
    'CALENDAR',
    'ORGANIZATION',
    'INVOICE',
    'PATIENT',
    'SERVICE',
    'DRUG',
    'PRESCRIPTION',
    'KANBAN',
  ],
  [TEAM_MEMBER_ROLE_LABELS.DOCTOR]: [
    'USER',
    'CALENDAR',
    'ORGANIZATION',
    'INVOICE',
    'PATIENT',
    'SERVICE',
    'DRUG',
    'KANBAN',
  ],
  [TEAM_MEMBER_ROLE_LABELS.NURSE]: [
    'USER',
    'CALENDAR',
    'ORGANIZATION',
    'INVOICE',
    'PATIENT',
    'SERVICE',
    'DRUG',
    'PRESCRIPTION',
    'KANBAN',
  ],
  [TEAM_MEMBER_ROLE_LABELS.ACCOUNTANT]: [
    'USER',
    'CALENDAR',
    'ORGANIZATION',
    'INVOICE',
    'PATIENT',
    'SERVICE',
    'DRUG',
    'KANBAN',
  ],
  // USER WHO IS NOT SUBSCRIBED OR DOES NOT HAVE A TEAM ROLE
  [USER_ROLES_LABELS.USER]: ['USER'],
});

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  patient: icon('ic_patient'),
  service: icon('ic_service'),
  faceMask: icon('ic_faceMask'),
  userPlus: icon('ic_userPlus'),
  prescription: icon('ic_prescription'),
  drug: icon('ic_drug'),
  organization: icon('ic_organization'),
  customer: icon('ic_customer'),
  subscription: icon('ic_subscription'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useTranslate();

  const { user } = useAuthContext();

  const { subscription } = useGetSubscriptionByUser();

  const navItems = useMemo(() => {
    const allItems = NAV_ITEMS(t);
    const roleNavMapping = ROLE_NAV_MAPPING(t);

    // Determine applicable roles
    // If user is super admin, use SUPER_ADMIN role
    // If user is ADMIN but they are also the part of another team which they don't own, use the team member role
    // If user is ADMIN, use ADMIN role when they are the owner of the team (user?.currentTeamRole === TEAM_MEMBER_ROLE_LABELS.OWNER)
    // If user is ADMIN but not part of any team, use ADMIN role
    // If user is not subscribed but part of a team, use the team member role
    // If user is not part of any team, use USER role
    // If user is not part of a team and not subscribed, use USER role
    // Determine applicable roles
    const roles = (() => {
      // Check if the user is a super admin
      if (user?.isSuperAdmin) {
        return roleNavMapping.SUPER_ADMIN;
      }

      // Check if the user is an admin
      if (user?.role === USER_ROLES_LABELS.ADMIN) {
        // If the user is the owner of the team
        if (user?.currentTeamRole === TEAM_MEMBER_ROLE_LABELS.OWNER) {
          return roleNavMapping.ADMIN;
        }
        // If the user is part of another team (not the owner)
        if (user?.currentTeamRole) {
          return roleNavMapping[user.currentTeamRole];
        }
        // If the user is not part of any team
        return roleNavMapping.ADMIN; // Admin without a team still retains ADMIN role
      }

      // If the user is not subscribed but part of a team
      if (user?.currentTeamRole) {
        if (user?.isCreatorOfCurrentTeam) {
          if (subscription?.status !== SUBSCRIPTION_STATUS.EXPIRED) {
            return roleNavMapping[user.currentTeamRole];
          }
          return roleNavMapping.USER; // Not subscribed, assign USER role
        }
        return roleNavMapping[user.currentTeamRole];
      }

      // If the user is not part of any team
      if (!user?.currentTeamRole) {
        return roleNavMapping.USER; // Not part of any team, assign USER role
      }

      // Default case: if none of the above conditions are met
      return roleNavMapping.USER; // Not part of a team and not subscribed, assign USER role
    })();

    // Filter children for specific roles
    const filterChildren = (item, role, isSuperAdmin, isCreator, userRole) => {
      if (item.path === paths.dashboard.organization.root) {
        if (
          role !== TEAM_MEMBER_ROLE_LABELS.OWNER &&
          role !== USER_ROLES_LABELS.ADMIN &&
          !isSuperAdmin
        ) {
          // If not OWNER, ADMIN, SUPER_ADMIN, remove create and edit e.g. [RECEPTIONIST, DOCTOR, NURSE, ACCOUNTANT, MEMBER]
          // Allow only root page of ORGANIZATION
          if (userRole === USER_ROLES_LABELS.ADMIN) {
            return {
              ...item,
              children: item.children?.filter(
                (child) => child.path !== paths.dashboard.organization.edit
              ),
            };
          }
          return {
            ...item,
            children: item.children?.filter(
              (child) => child.path === paths.dashboard.organization.root
            ),
          };
        }
        if (role === TEAM_MEMBER_ROLE_LABELS.OWNER && userRole !== USER_ROLES_LABELS.ADMIN) {
          // If OWNER, and not ADMIN, remove create e.g. [OWNER]
          return {
            ...item,
            children: item.children?.filter(
              (child) => child.path !== paths.dashboard.organization.new
            ),
          };
        }
        // If OWNER, ADMIN, SUPER_ADMIN show all e.g. [OWNER], unless the team is not created yet
        if (!user?.currentTeamRole) {
          return {
            ...item,
            children: item.children?.filter(
              (child) => child.path !== paths.dashboard.organization.edit
            ),
          };
        }
      }
      if (item.path === paths.dashboard.user.root) {
        if (!isSuperAdmin) {
          // If not SUPER_ADMIN, remove list e.g. [RECEPTIONIST, DOCTOR, NURSE, ACCOUNTANT, MEMBER]
          return {
            ...item,
            children: item.children?.filter((child) => child.path === paths.dashboard.user.account),
          };
        }
      }
      if (item.path === paths.dashboard.invoice.root) {
        if (role !== TEAM_MEMBER_ROLE_LABELS.RECEPTIONIST
          && role !== TEAM_MEMBER_ROLE_LABELS.OWNER
          && role !== USER_ROLES_LABELS.ADMIN
          // && role !== TEAM_MEMBER_ROLE_LABELS.ACCOUNTANT
          && !isSuperAdmin
        ) {
          // If not RECEPTIONIST or OWNER, remove create e.g. [ACCOUNTANT, DOCTOR, NURSE, MEMBER]
          return {
            ...item,
            children: item.children?.filter((child) => child.path === paths.dashboard.invoice.root),
          };
        }
      }
      if (item.path === paths.dashboard.patient.root) {
        if (
          role !== TEAM_MEMBER_ROLE_LABELS.OWNER &&
          role !== USER_ROLES_LABELS.ADMIN &&
          role !== TEAM_MEMBER_ROLE_LABELS.NURSE &&
          role !== TEAM_MEMBER_ROLE_LABELS.DOCTOR &&
          !isSuperAdmin
        ) {
          // If not NURSE or DOCTOR, remove create e.g. [RECEPTIONIST, ACCOUNTANT, MEMBER]
          return {
            ...item,
            children: item.children?.filter((child) => child.path === paths.dashboard.patient.root),
          };
        }
      }
      if (item.path === paths.dashboard.service.root) {
        if (role !== TEAM_MEMBER_ROLE_LABELS.OWNER
          && role !== USER_ROLES_LABELS.ADMIN
          && !isSuperAdmin
        ) {
          // If not OWNER or ADMIN, remove create e.g. [RECEPTIONIST, ACCOUNTANT, MEMBER]
          return {
            ...item,
            children: item.children?.filter((child) => child.path === paths.dashboard.service.root),
          };
        }
      }
      if (item.path === paths.dashboard.drug.root) {
        if (role !== TEAM_MEMBER_ROLE_LABELS.OWNER
          && role !== USER_ROLES_LABELS.ADMIN
          && role !== TEAM_MEMBER_ROLE_LABELS.DOCTOR
          && !isSuperAdmin
        ) {
          // If not OWNER or DOCTOR, remove create e.g. [RECEPTIONIST, ACCOUNTANT, MEMBER]
          return {
            ...item,
            children: item.children?.filter((child) => child.path === paths.dashboard.drug.root),
          };
        }
      }
      return item;
    };

    // Map and filter items based on roles and permissions
    return roles.map((roleKey) => {
      const item = allItems[roleKey];
      return filterChildren(
        item,
        user?.currentTeamRole || user?.role,
        user?.isSuperAdmin,
        user?.isCreatorOfCurrentTeam,
        user?.role
      );
    });
  }, [subscription?.status, t, user]);

  const data = useMemo(
    () => [
      // OVERVIEW
      // ----------------------------------------------------------------------
      {
        subheader: t('overview'),
        items: [
          {
            title: t('app'),
            path: paths.dashboard.root,
            icon: ICONS.dashboard,
          },
          {
            title: t('analytics'),
            path: paths.dashboard.general.analytics,
            icon: ICONS.analytics,
          },
          // {
          //   title: t('banking'),
          //   path: paths.dashboard.general.banking,
          //   icon: ICONS.banking,
          // },
          {
            title: t('file'),
            path: paths.dashboard.general.file,
            icon: ICONS.file,
          },
        ],
      },

      // MANAGEMENT
      // ----------------------------------------------------------------------
      {
        subheader: t('management'),
        items: navItems,
      },
    ],
    [navItems, t]
  );

  return data;
}
